import { BodyText, Button, Stack } from '@servicetitan/design-system';
import { FC } from 'react';

interface PaginationBaseProps {
    total: number;
    skip: number;
    take: number;
    className?: string;
}

interface PaginationProps extends PaginationBaseProps {
    update(skip: number): void;
}

export const Pagination: FC<PaginationProps> = ({ className, skip, take, total, update }) => {
    const maxPage = Math.floor(total / take) - (total % take === 0 ? 1 : 0);
    const maxSkip = maxPage * take;
    const handleSkipToStart = () => update(0);
    const handleSkipToEnd = () => update(maxSkip);
    const handleNextPage = () => update(Math.min(skip + take, maxSkip));
    const handlePreviousPage = () => update(Math.max(skip - take, 0));
    const handleChangePage = (page: number) => update(page * take);

    const page = Math.floor(skip / take);
    const pages = [page - 2, page - 1, page, page + 1, page + 2].filter(
        p => p >= 0 && p <= maxPage
    );

    if (!pages.length) {
        pages.push(0);
    } else if (pages.length < 5) {
        if (pages[0] > 0) {
            pages.unshift(pages[0] - 1);

            if (pages[0] > 0) {
                pages.unshift(pages[0] - 1);
            }
        }

        if (pages[pages.length - 1] < maxPage) {
            pages.push(pages[pages.length - 1] + 1);

            if (pages[pages.length - 1] < maxPage) {
                pages.push(pages[pages.length - 1] + 1);
            }
        }
    }

    return (
        <Stack spacing={1} justifyContent="space-between" alignItems="center" className={className}>
            <Stack alignItems="center">
                <Button
                    iconName="skip_previous"
                    aria-label="First"
                    fill="subtle"
                    size="xsmall"
                    onClick={handleSkipToStart}
                    disabled={skip === 0}
                />
                <Button
                    iconName="arrow_left"
                    aria-label="Previous"
                    fill="subtle"
                    size="xsmall"
                    disabled={skip === 0}
                    onClick={handlePreviousPage}
                />
                {pages.map(p => (
                    <Button
                        primary
                        key={p}
                        size="xsmall"
                        fill={p !== page ? 'subtle' : undefined}
                        onClick={() => handleChangePage(p)}
                        disabled={pages.length === 1}
                    >
                        {p + 1}
                    </Button>
                ))}
                <Button
                    iconName="arrow_right"
                    aria-label="Next"
                    fill="subtle"
                    size="xsmall"
                    disabled={skip === maxSkip}
                    onClick={handleNextPage}
                />
                <Button
                    iconName="skip_next"
                    aria-label="Last"
                    fill="subtle"
                    size="xsmall"
                    disabled={skip === maxSkip}
                    onClick={handleSkipToEnd}
                />
            </Stack>
        </Stack>
    );
};

export const PaginationCounter: FC<PaginationBaseProps> = ({ className, skip, take, total }) => {
    const page = Math.floor(skip / take);
    const maxPage = Math.floor(total / take);
    const visible = page === maxPage ? total - skip : take;

    return (
        <BodyText size="small" className={className}>
            {skip + 1}-{skip + visible} of {total}
        </BodyText>
    );
};

export const PaginationFooter: FC<PaginationProps> = ({ className, skip, take, total, update }) => (
    <Stack justifyContent="space-between" alignItems="center" className={className}>
        <Pagination total={total} skip={skip} take={take} update={update} />
        <PaginationCounter total={total} skip={skip} take={take} />
    </Stack>
);
